/**
 * PotholeDetectionResults
 *
 * Design: Road hazard focused with severity visualization
 * - Severity distribution with color-coded bars
 * - Prioritized potholes list with severity badges
 * - Infrastructure damage assessment styling
 */
import React from "react";
import { PotholeDetectionResults as ResultsType } from "../../types";
import { formatNumber } from "../../utils";
import {
  AlertTriangle,
  Circle,
  Target,
  List,
  Shield,
  AlertCircle,
  CheckCircle,
} from "lucide-react";

interface PotholeDetectionResultsProps {
  results: ResultsType;
}

export const PotholeDetectionResults: React.FC<
  PotholeDetectionResultsProps
> = ({ results }) => {
  const isVideo = results.type === "video";
  const totalPotholes = isVideo
    ? results.total_potholes_detected || 0
    : results.potholes_detected || 0;
  const { severe = 0, moderate = 0, minor = 0 } = results.severity_distribution;
  const total = severe + moderate + minor;

  const getSeverityIcon = (severity: string) => {
    switch (severity.toLowerCase()) {
      case "severe":
        return <AlertTriangle className="w-4 h-4" />;
      case "moderate":
        return <AlertCircle className="w-4 h-4" />;
      case "minor":
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <Circle className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Hero Stats */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-orange-500/20 via-slate-800/50 to-slate-900 border border-orange-500/30 p-8">
        <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-12 -right-12">
          <AlertTriangle className="w-48 h-48 text-orange-500/5" />
        </div>

        <div className="relative z-10 flex items-center gap-6">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-lg shadow-orange-500/30">
            <AlertTriangle className="w-10 h-10 text-white" />
          </div>
          <div>
            <p className="text-slate-400 text-sm font-medium mb-1">
              Total Potholes Detected
            </p>
            <h2 className="text-6xl font-bold text-white tracking-tight">
              {formatNumber(totalPotholes)}
            </h2>
            <p className="text-orange-400 text-sm mt-2">
              Road hazards requiring attention
            </p>
          </div>
        </div>
      </div>

      {/* Severity Distribution */}
      <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
            <Shield className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h3 className="text-white font-semibold">Severity Distribution</h3>
            <p className="text-slate-400 text-sm">
              Damage assessment by severity level
            </p>
          </div>
        </div>

        {/* Severity Bar */}
        <div className="mb-6">
          <div className="h-4 bg-slate-700/50 rounded-full overflow-hidden flex">
            {severe > 0 && (
              <div
                className="bg-red-500 h-full"
                style={{ width: `${(severe / total) * 100}%` }}
              />
            )}
            {moderate > 0 && (
              <div
                className="bg-amber-500 h-full"
                style={{ width: `${(moderate / total) * 100}%` }}
              />
            )}
            {minor > 0 && (
              <div
                className="bg-emerald-500 h-full"
                style={{ width: `${(minor / total) * 100}%` }}
              />
            )}
          </div>
        </div>

        {/* Severity Cards */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-red-500/10 rounded-xl p-5 border border-red-500/30 text-center">
            <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center mx-auto mb-3">
              <AlertTriangle className="w-5 h-5 text-red-400" />
            </div>
            <p className="text-4xl font-bold text-red-400">{severe}</p>
            <p className="text-slate-400 text-sm mt-1">Severe</p>
            <p className="text-red-400/60 text-xs mt-1">Immediate repair</p>
          </div>

          <div className="bg-amber-500/10 rounded-xl p-5 border border-amber-500/30 text-center">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center mx-auto mb-3">
              <AlertCircle className="w-5 h-5 text-amber-400" />
            </div>
            <p className="text-4xl font-bold text-amber-400">{moderate}</p>
            <p className="text-slate-400 text-sm mt-1">Moderate</p>
            <p className="text-amber-400/60 text-xs mt-1">Schedule repair</p>
          </div>

          <div className="bg-emerald-500/10 rounded-xl p-5 border border-emerald-500/30 text-center">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center mx-auto mb-3">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
            </div>
            <p className="text-4xl font-bold text-emerald-400">{minor}</p>
            <p className="text-slate-400 text-sm mt-1">Minor</p>
            <p className="text-emerald-400/60 text-xs mt-1">Monitor</p>
          </div>
        </div>
      </div>

      {/* Prioritized Potholes (Video) */}
      {isVideo &&
        results.prioritized_potholes &&
        results.prioritized_potholes.length > 0 && (
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center">
                <Target className="w-5 h-5 text-primary-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold">
                  Prioritized Potholes
                </h3>
                <p className="text-slate-400 text-sm">
                  Sorted by repair priority score
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {results.prioritized_potholes.slice(0, 10).map((pothole) => (
                <div
                  key={pothole.id}
                  className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50 hover:border-primary-500/30 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          pothole.severity === "severe"
                            ? "bg-red-500/20 text-red-400"
                            : pothole.severity === "moderate"
                            ? "bg-amber-500/20 text-amber-400"
                            : "bg-emerald-500/20 text-emerald-400"
                        }`}
                      >
                        {getSeverityIcon(pothole.severity)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-white font-medium">
                            Pothole #{pothole.id}
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                              pothole.severity === "severe"
                                ? "bg-red-500/20 text-red-400"
                                : pothole.severity === "moderate"
                                ? "bg-amber-500/20 text-amber-400"
                                : "bg-emerald-500/20 text-emerald-400"
                            }`}
                          >
                            {pothole.severity}
                          </span>
                          <span className="px-2 py-0.5 rounded-full text-xs bg-slate-700/50 text-slate-400 capitalize">
                            {pothole.size_category}
                          </span>
                        </div>
                        <p className="text-slate-500 text-xs mt-1">
                          Position: [
                          {pothole.bbox.map((n) => n.toFixed(0)).join(", ")}]
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-primary-400">
                        {pothole.priority_score.toFixed(1)}
                      </p>
                      <p className="text-slate-500 text-xs">priority</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      {/* All Potholes (Image) */}
      {!isVideo && results.potholes && results.potholes.length > 0 && (
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
                <List className="w-5 h-5 text-violet-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold">All Potholes</h3>
                <p className="text-slate-400 text-sm">
                  {results.potholes.length} potholes detected
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {results.potholes.map((pothole) => (
              <div
                key={pothole.id}
                className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50"
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      pothole.severity === "severe"
                        ? "bg-red-500/20 text-red-400"
                        : pothole.severity === "moderate"
                        ? "bg-amber-500/20 text-amber-400"
                        : "bg-emerald-500/20 text-emerald-400"
                    }`}
                  >
                    {getSeverityIcon(pothole.severity)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium text-sm">
                        Pothole #{pothole.id}
                      </span>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs capitalize ${
                          pothole.severity === "severe"
                            ? "bg-red-500/20 text-red-400"
                            : pothole.severity === "moderate"
                            ? "bg-amber-500/20 text-amber-400"
                            : "bg-emerald-500/20 text-emerald-400"
                        }`}
                      >
                        {pothole.severity}
                      </span>
                    </div>
                  </div>
                  <span className="text-emerald-400 text-sm font-medium">
                    {(pothole.confidence * 100).toFixed(0)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
